<template>
  <div class="micCheck" :class="{ compact }">
    <template v-if="named">
      <p v-if="allowedEarlier" class="micCopy">Already allowed in this browser.</p>
      <div class="micRow">
        <span class="micBadge" aria-hidden="true"><AppIcon name="microphone" :size="compact ? 14 : 18" /></span>
        <span class="micName">{{ micName(device) }}</span>
      </div>
      <p class="micHint">
        To switch mics or check your level in Chrome, click the icon at the left of the address bar, then
        <b class="uiLabel">Microphone</b>.
      </p>
      <div v-if="$slots.action" class="micActions"><slot name="action" /></div>
    </template>
    <template v-else>
      <p v-if="state === 'dismissed'" class="micCopy">
        The browser prompt closed before you chose. Try again and pick <b class="uiLabel">Allow</b>.
      </p>
      <p v-else-if="state === 'blocked'" class="micCopy">
        Your browser is blocking the microphone for this site. If you're using Chrome, click the icon at the
        left of the address bar, turn <b class="uiLabel">Microphone</b> on, then press Try again.
      </p>
      <p v-else-if="problem === 'no_device'" class="micCopy">
        We couldn’t find a microphone. Plug in a headset or connect a mic, then try again.
      </p>
      <p v-else-if="problem === 'busy'" class="micCopy">
        Your microphone is busy or unavailable. Close other apps that might be using it, then try again.
      </p>
      <p v-else-if="problem === 'system_blocked'" class="micCopy">
        Your computer isn’t letting the browser use the microphone. Allow it in your system privacy settings
        (on a Mac: System Settings → Privacy &amp; Security → Microphone), then try again.
      </p>
      <p v-else-if="problem === 'unsupported'" class="micCopy">
        This browser can’t use a microphone on this page. Open the dialer in an up-to-date browser.
      </p>
      <p v-else-if="state === 'requesting'" class="micCopy">
        So prospects can hear you. Choose <b class="uiLabel">Allow</b> in your browser’s prompt.
      </p>
      <p v-else-if="state === 'prompt'" class="micCopy">So prospects can hear you. Your browser will ask once.</p>
      <div v-if="askLabel" class="micActions">
        <button
          type="button"
          class="btn"
          :class="compact ? 'secondary' : 'primary'"
          :disabled="state === 'requesting'"
          @click="request()"
        >
          <AppIcon name="microphone" :size="14" />{{ askLabel }}
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AppIcon from '../../../components/AppIcon.vue'
import { micName, useMicPermission } from '../../composables/useMicPermission'

// What the browser says about the mic, and the one thing the rep can do about it: asking here is always
// their click. Whether a prompt also opens on its own is the caller's (useMicReading).
withDefaults(defineProps<{ compact?: boolean; allowedEarlier?: boolean }>(), { compact: false, allowedEarlier: false })

const { state, problem, device, request } = useMicPermission()

const named = computed(() => state.value === 'granted' && device.value !== null)
const askLabel = computed(() => {
  if (state.value === 'requesting') return 'Waiting for your browser…'
  if (state.value === 'prompt') return 'Allow microphone'
  if (state.value === 'granted' || state.value === 'unknown' || problem.value === 'unsupported') return null
  return 'Try again'
})
</script>

<style scoped>
.micCheck { display: flex; flex-direction: column; gap: var(--space-5); min-width: 0; }
.micCopy { margin: 0; font-size: var(--fs-base); line-height: var(--lh-loose); color: var(--color-text-secondary); }
.micHint { margin: 0; font-size: var(--fs-sm); line-height: var(--lh-normal); color: var(--color-text-tertiary); }
.uiLabel { font-weight: var(--fw-semibold); color: var(--color-text-primary); }
.micRow { display: flex; align-items: center; gap: var(--space-4); min-width: 0; padding: var(--space-4); border: 1px solid var(--color-border-subtle); border-radius: var(--radius-md); background: var(--color-bg-app); }
.micBadge { width: 40px; height: 40px; border-radius: var(--radius-full); background: var(--color-success-soft); color: var(--color-success); display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
.micName { min-width: 0; font-weight: var(--fw-semibold); color: var(--color-text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.micActions { display: flex; align-items: center; justify-content: flex-end; gap: var(--space-4); padding-top: var(--space-2); }

/* Inside a Preferences card, where the mic is a setting at rest: the badge is neutral, and keeps its
   green only in onboarding. */
.compact { gap: var(--space-3); }
.compact .micCopy, .compact .micHint { font-size: var(--fs-xs); line-height: var(--lh-normal); color: var(--color-text-tertiary); }
.compact .micRow { gap: var(--space-3); padding: 0; border: 0; background: transparent; }
.compact .micBadge { width: 26px; height: 26px; background: var(--color-bg-subtle); color: var(--color-text-tertiary); }
.compact .micName { font-size: var(--fs-sm); }
.compact .micActions { justify-content: flex-start; padding-top: 0; }
</style>
